import React, { useState, useEffect, useRef, useId } from "react";
import { AiOutlineCheck } from "react-icons/ai";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Branch, ProductBranch } from "../../types/globalTypes";

interface ProductBranchesInputProps {
  userPrevSelectedBranches: ProductBranch[];
  allUserBranches: Branch[];
  placeholder: string;
  onBranchChange: (selectedBranches: ProductBranch[]) => void;
  error?: string | string[] | undefined;
}

const ProductBranchesInput: React.FC<ProductBranchesInputProps> = ({
  userPrevSelectedBranches,
  placeholder,
  onBranchChange,
  error,
  allUserBranches,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language


  const allUserBranchesForInput: ProductBranch[] = allUserBranches.map((branch) => ({id: branch._id, nameEN: branch.nameEN , nameAR: branch.nameAR, isMain: branch.isMain}));

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ProductBranch[]>(userPrevSelectedBranches);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const id = useId();

  const toggleDropdown = () => setIsOpen((prev) => !prev);




  const handleSelect = (option: ProductBranch) => {
    const isSelected = selected.some((item) => item.id === option.id);
    const newSelected = isSelected
      ? selected.filter((item) => item.id !== option.id)
      : [...selected, option];

    setSelected(newSelected);
    onBranchChange(newSelected);
  };

  const handleRemove = (option: ProductBranch) => {
    const newSelected = selected.filter((item) => item.id !== option.id);
    setSelected(newSelected);
    onBranchChange(newSelected);
  };

  const handleToggleSelectAllBranches = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selected.length === 0 && allUserBranchesForInput.length === 0) return

    if (selected.length === allUserBranchesForInput.length) {
      setSelected([]);
      onBranchChange([]);
    } else {
      setSelected(allUserBranchesForInput);
      onBranchChange(allUserBranchesForInput);
    }
  } 

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);
  

  return (
    <div className="relative w-full">
      <select
        multiple
        value={selected.map((item) => item.id)}
        id={`${id}-Branchselect`}
        name={`${id}-Branchselect`}
        onChange={(e) =>
          setSelected(
            Array.from(e.target.selectedOptions).map(
              (option) =>
                allUserBranchesForInput.find((item) => item.id === option.value)!
            )
          )
        }
        className="pointer-events-none absolute opacity-0"
      >
        {allUserBranchesForInput.map((option) => (
          <option key={option.id} value={option.id}>
            {locale === "en" ? option.nameEN || option.nameAR : option.nameAR || option.nameEN}
          </option>
        ))}
      </select>

      {/* Branches Input Box */}
      <div
        className={`flex min-h-12 cursor-pointer items-center justify-between rounded-sm border border-gray-200  md:min-h-12 ${selected.length === 0 && allUserBranchesForInput.length === 0
          ? "text-gray-500 cursor-not-allowed"
          : selected.length === allUserBranchesForInput.length
          ? "text-green-600"
          : "text-gray-500"
        }`}
        onClick={toggleDropdown}
        ref={inputRef}  
      >

        <div className="flex flex-grow flex-wrap gap-1 px-2 py-[2px] text-gray-600">
          {selected.length > 0 ? (
            selected.map((item) => (
              <div
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(item);
                }}
                className="inline-flex items-center rounded-sm bg-skyblue text-sidebar font-semibold px-2 py-1 text-xs "
              >
                <span>{locale === "en" ?item.nameEN || item.nameAR: item.nameAR || item.nameEN}</span>
                <span className="text-voilet-800 ms-2 mt-[1px] cursor-pointer text-[10px]">
                  &#10005;
                </span>
              </div>
            ))
          ) : (
            <span className="ps-2 text-sm text-gray-500">{placeholder}</span>
          )}
        </div>
        <span className="text-sm text-gray-400 pe-2">
          <MdOutlineKeyboardArrowDown size={18} />
        </span>
        {/* Available in All Branches */}
        <button
        onClick={handleToggleSelectAllBranches}
        type="button" className={`border-s  border-s-gray-200 px-2 min-h-12 h-full ${selected.length === 0 && allUserBranchesForInput.length === 0 ? "cursor-not-allowed" : ""}`}>
        {t("branchAvailability.allBranches")}
                  </button>
      </div>

      {/* Custom Dropdown */}
      {isOpen && (
        <div
          className="absolute left-0 right-0 top-full z-10 max-h-52 overflow-y-auto thin-scrollbar rounded-sm border border-gray-200 bg-white shadow-md"
          ref={dropdownRef}
        >
          <ul className="m-0 list-none p-0">
            {allUserBranchesForInput.length > 0 ? (
              allUserBranchesForInput.map((option) => (
                <li
                  key={option.id}
                  className={`flex cursor-pointer items-center justify-between px-2 py-2 ${
                    selected.some((item) => item.id === option.id)
                      ? "bg-sky-100"
                      : "hover:bg-sky-100"
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {locale === "en" ?option.nameEN || option.nameAR: option.nameAR || option.nameEN}
                  {option.isMain && (
                    <span className="text-sm text-gray-400">
                    { t("branchAvailability.mainBranch")}
                    </span>
                  )}
                  {selected.some((item) => item.id === option.id) && (
                    <AiOutlineCheck className="text-black" size={12} />
                  )}
                </li>
              ))
            ) : (
              <li className="p-2 text-center text-gray-500">
                       { t("branchAvailability.none")}
              </li>
            )}
          </ul>
        </div>
      )}
      {error && <div className="text-xs text-red-500">{error}</div>}
    </div>
  );
};

export default ProductBranchesInput;
